import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import Brand from "@/components/Brand";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { usePlatformSettings } from "@/context/PlatformContext";

export default function Login() {
  const { login, user } = useAuth();
  const platform = usePlatformSettings();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Sign in failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1724847664831-27b55fef3121?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHx3ZWRkaW5nJTIwZmxvd2VycyUyMGRlY29yYXRpb24lMjBwcmVtaXVtfGVufDB8fHx8MTc4NDY1NzI3Mnww&ixlib=rb-4.1.0&q=85"
          alt="Business decor"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1a0f08]/85 via-[#2a1810]/50 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src={platform.logo_url || "/logo.png"} alt="Vyapar" className="w-11 h-11 rounded-lg object-cover bg-white/95 p-1" />
            <span className="font-heading font-bold text-xl tracking-tight">Vyapar CRM</span>
          </div>
          <div>
            <div className="overline text-white/70 mb-3">Connect · Manage · Grow</div>
            <h1 className="font-heading font-extrabold text-5xl leading-none tracking-tight mb-4" data-testid="login-headline">
              {platform.homepage_headline || "Every deal, every rupee, tracked."}
            </h1>
            <p className="text-white/80 max-w-md leading-relaxed">
              A modern CRM built for growing Indian businesses — leads, orders, payments and follow-ups in one refined workspace.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form" autoComplete="off">
          <div className="lg:hidden mb-2"><Brand /></div>
          <div>
            <div className="overline text-muted-foreground mb-2">Sign in</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Welcome back</h2>
            <p className="text-sm text-muted-foreground mt-1">Access your Vyapar CRM workspace.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs">EMAIL</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required autoComplete="off" placeholder="you@company.com"
                data-testid="login-email-input" className="mt-1.5" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs">PASSWORD</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot?</Link>
              </div>
              <div className="relative mt-1.5">
                <Input id="password" type={show ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password"
                  placeholder="Enter your password"
                  data-testid="login-password-input" />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="toggle password">
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit-button"
            className="w-full rounded-full h-11">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              <span className="bg-background px-2">or</span>
            </div>
          </div>
          <GoogleSignInButton label="Continue with Google" testId="login-google-button" />
          <div className="text-sm text-center text-muted-foreground">
            New to Vyapar CRM?{" "}
            <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="signup-link">Create an account</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
